import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { TargetList } from '@/components/logbook/target/TargetList';
import { DriveOnStatusDto, GetTargetsWithDistanceParams, DriveOnTargetDto as Target } from '@/generated/model';
import { changeTarget, getLatestDriveOnDriverInput, getTargets, getTargetsWithDistance } from '@/generated/client/driveon-api/driveon-api';
import { useDriveOnContext } from '@/contexts/DriveOnContext';
import { useNotificationContext } from '@/contexts/NotificationCtx';
import { VehicleDialog } from '@/components/logbook/target/TargetConfirmationDialog';
import { Autocomplete, Box, Checkbox, CircularProgress, FormControlLabel, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LoadingOverlay } from '@/components/common/LoadingOverlay';
import { DriveOnDriverInputInputType as InputType } from '@/generated/model/driveOnDriverInputInputType';
import { Logbook } from '@/model/Logbook';

import './ChooseTarget.css';
export function ChooseTarget() {
  const { t } = useTranslation();
  const driveonCtx = useDriveOnContext();
  const notifyCtx = useNotificationContext();
  const navigate = useNavigate();
  
  const [targets, setTargets] = useState<Target[] | undefined>(undefined);
  const [targetsHistory, setTargetsHistory] = useState<Target[] | undefined>(undefined);
  const [nearestTargets, setNearestTargets] = useState<Target[] | undefined>(undefined);
  const [dialog, setDialog] = useState<ReactElement | undefined>(undefined);
  const [filterEgs, setFilterEgs] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleChangeChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterEgs(event.target.checked);
  };

   useEffect(() => {
      const contextIsNotLoaded = !driveonCtx.inputType && !driveonCtx.logbook && !driveonCtx.target && !driveonCtx.task;
      async function reloadPageContent() {
        if (contextIsNotLoaded) {
          const result = await getLatestDriveOnDriverInput();
  
          if (result && result.status == 200) {
            const data = result.data;
            updateDriveOnContext(data);
            console.log('reloadPageContent(): Api called: getLatestDriveOnDriverInput');
          }
        }
      }
  
      function updateDriveOnContext(data: DriveOnStatusDto) {
        const inputType = data.driverInput?.inputType;
        driveonCtx.setInputType(inputType);
  
        if (inputType === InputType.TARGET_UNASSIGNED) {
          eraseContext();
        } else if (inputType === InputType.MIDNIGHT_CLEANUP) {
          eraseContext();
        } else if (inputType === InputType.KICKED) {
          eraseContext();
        } else {
          updateContext(data)
        }
      }
  
      function eraseContext() {
        driveonCtx.setTarget(undefined);
        driveonCtx.setTask(undefined);
        driveonCtx.setLogbook(undefined);
      }
  
      function updateContext(data: DriveOnStatusDto) {
        const driverInput = data.driverInput;
        const task = data.task;
        const target = data?.driverInput?.target;
        driveonCtx.setTarget(target);
        driveonCtx.setTask(task);
        driveonCtx.setLogbook({
          taskName: driverInput?.task?.name,
          destination: driverInput?.destination,
          productDescription: driverInput?.productDescription,
          productWeight: driverInput?.productWeight,
          personCount: driverInput?.personCount,
          trailer: driverInput?.trailer,
          workingHours: driverInput?.target.egs ? 0 : driverInput?.workingHours,
          heatingHours: driverInput?.target.egs ? 0 : driverInput?.heatingHours,
          comment: driverInput?.comment,
        } satisfies Logbook);
      }
    
      reloadPageContent();
    }, []);

  useEffect(() => {

    async function loadAllTargets() {
      const response = await getTargets().catch((error) => {
          setTargets(undefined);
          setTargetsHistory(undefined);
          console.log('errrorCallback(): Api failed: getTargets', error);
        });

        if (response && response.status == 200) {
          const data = response.data;
          setTargets(data.allTargets);
          setTargetsHistory(data.historyTargets);
          console.log('errrorCallback(): Api called: getTargets');
        }      
    }

    async function loadNearestTargets() {
      navigator.geolocation.getCurrentPosition(
        (position) => successCallback(position),
        () => errrorCallback()
      );

      async function errrorCallback() {
        notifyCtx?.showNotification('info', t('menetlevel.choosevehicle.nolocation'), undefined, 5);
        setNearestTargets([]);
      }

      async function successCallback(position: GeolocationPosition) {
        const pos = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        } satisfies GetTargetsWithDistanceParams;

        const response = await getTargetsWithDistance(pos).catch((error) => {
          setNearestTargets(undefined);
          console.log('successCallback(): Api failed: getTargets', error);
        });

        if (response && response.status == 200) {
          const data = response.data;
          setNearestTargets(data.nearestTargets);
          console.log('successCallback(): Api called: getTargets');
        }
      }
    }

    loadAllTargets()
    loadNearestTargets();
  }, []);

  function displayTargetLists() {
    const filteredTargetsHistory = targetsHistory?.filter((t) => t.id !== driveonCtx.target?.id);
    const egsFilteredTargetsHistory = filteredTargetsHistory?.filter((t) => t.egs);

    const filteredNearestTargets = nearestTargets?.filter((t) => t.id !== driveonCtx.target?.id);
    const egsFilteredNearestTargets = filteredNearestTargets?.filter((t) => t.egs);

    if (nearestTargets && targetsHistory) {
      return (
        <>
          {displayTargetHistory(egsFilteredTargetsHistory, filteredTargetsHistory, false)}
          {displayTargetNearest(egsFilteredNearestTargets, filteredNearestTargets, false)}
        </>
      );
    } else if (nearestTargets && !targetsHistory) {
      return (
        <>
          {displayTargetHistory(egsFilteredTargetsHistory, filteredTargetsHistory, true)}
          {displayTargetNearest(egsFilteredNearestTargets, filteredNearestTargets, false)}
        </>
      );
    } else if (!nearestTargets && targetsHistory) {
      return (
        <>
          {displayTargetHistory(egsFilteredTargetsHistory, filteredTargetsHistory, false)}
          {displayTargetNearest(egsFilteredNearestTargets, filteredNearestTargets, true)}
        </>
      );
    }
    else {
      return (
      <>
        {displayTargetHistory(egsFilteredTargetsHistory, filteredTargetsHistory, true)}
        {displayTargetNearest(egsFilteredNearestTargets, filteredNearestTargets, true)}
      </>
    );
    }
  }

  function displayTargetNearest(egsFilteredNearestTargets: Target[] | undefined, filteredNearestTargets: Target[] | undefined, loading: boolean) {
    if (loading) {
        return (<div className="target-list">
          <div className="title">{t('menetlevel.choosevehicle.proximity')}</div>
            <Box sx={{ display: 'flex' }}>
              <CircularProgress />
            </Box>
        </div>);
    }
    
    return <TargetList
          title={t('menetlevel.choosevehicle.proximity')}
          targets={filterEgs ? egsFilteredNearestTargets : filteredNearestTargets}
          setTarget={setTarget}
          distance={true}
          targetsHistory={false}
        />;
  }

  function displayTargetHistory(egsFilteredTargetsHistory: Target[] | undefined, filteredTargetsHistory: Target[] | undefined, loading: boolean) {
    if (loading) {
        return (<div className="target-list">
          <div className="title">{t('menetlevel.choosevehicle.recentlyused')}</div>
            <Box sx={{ display: 'flex' }}>
              <CircularProgress />
            </Box>
        </div>);
    }
    
    return <TargetList
      title={t('menetlevel.choosevehicle.recentlyused')}
      targets={filterEgs ? egsFilteredTargetsHistory : filteredTargetsHistory}
      setTarget={setTarget}
      distance={false}
      targetsHistory={true} />;
  }

  async function setTarget(target: Target) {
    await handleChangeTarget(target, false, false);
  }

  async function handleChangeTarget(
    target: Target,
    targetCardIdConfirmed: boolean,
    forceChangeDriverConfirmed: boolean
  ) {
    setLoading(true);
    const result = await changeTarget({
      targetId: target.id,
      targetCardIdConfirmed: targetCardIdConfirmed,
      forceChangeDriverConfirmed: forceChangeDriverConfirmed,
    })
      .catch((error) => {
        const response = error.response.data;
        if (error.response.status == 404 && response.exceptionClass.includes('DriveOnAuthTemplateNotFoundException')) {
          handleChangeTarget(target, true, false);
          return;
        }
        if (error.response.status != 409) {
          throw error;
        }
        const message = response.message;
        const errorType: string = response.exceptionClass;
        if (errorType.includes('DriveOnTargetIdentificationException')) {
          function onClose() {
            setDialog(undefined);
          }
          function onAccept() {
            handleChangeTarget(target, true, false);
            setDialog(undefined);
          }
          setDialog(<VehicleDialog onClose={onClose} onAccept={onAccept} message={message} target={target} />);
        } else if (errorType.includes('DriveOnTargetAlreadyAssignedException')) {
          function onClose() {
            setDialog(undefined);
          }
          function onAccept() {
            handleChangeTarget(target, true, true);
            setDialog(undefined);
          }
          setDialog(<VehicleDialog onClose={onClose} onAccept={onAccept} message={message} target={target} />);
        } else {
          throw error;
        }
      })
      .finally(() => setLoading(false));
    if (result && result.status == 200) {
      notifyCtx?.showNotification('success', t('menetlevel.choosevehicle.takeover'), undefined, 5);
      driveonCtx.setTarget(target);
      driveonCtx.setInputType(InputType.TARGET_ASSIGNED);
      driveonCtx.setLogbook(undefined);
      driveonCtx.setTask(undefined);
      navigate(-1);
    }
  }

  function searchTargetsList(): readonly string[] {
    const targetNames = targets
      ?.filter((t) => t.id !== driveonCtx.target?.id)
      .map((t) => {
        const name: string = t.name ? t.name + ' [' + t.vehicleCategory + '] ' : '';
        const egs: string = t.egs ? '(EGS)' : '';
        return name + egs;
      })
      .filter((t) => t !== '');

    if (targetNames) {
      if (filterEgs) {
        return targetNames.filter((t) => t.includes('EGS'));
      }
      return targetNames;
    }
    return [];
  }

  return (
    <>
      <div className="choose-vehicle-container">
        <div className="title">
          <span>{t('menetlevel.choosevehicle.title')}</span>
        </div>
        <div className="description">{t('menetlevel.choosevehicle.description')}</div>
        <Autocomplete
          freeSolo
          id="free-solo-2-demo"
          disableClearable
          options={searchTargetsList()}
          value={inputValue}
          onChange={(event, newInputValue) => {
            event.preventDefault();
            setInputValue(newInputValue);
            const targetName: string = newInputValue.split(' ')[0];
            const t = targets?.find((t) => t.name === targetName);
            if (t) {
              setTarget(t);
            } else {
              notifyCtx?.showNotification('error', 'Nem sikerült kiválasztani a jármüvet.', undefined, 5);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Keresés"
              slotProps={{
                input: {
                  ...params.InputProps,
                  type: 'search',
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
        <FormControlLabel
          control={<Checkbox checked={filterEgs} onChange={handleChangeChecked} />}
          label={t('menetlevel.choosevehicle.egs.show')}
        />
        {displayTargetLists()}
        {dialog}
      </div>
      <LoadingOverlay loading={loading} />
    </>
  );
}
